/**
 * @remarks 实体模块，用于管理实体相关的配置和操作
 * @author ickeep <i@ickeep.com>
 * @module entity
 */

import { IAny, IAnyObject, IObjKey, IOptions, IPageData, isSuccess, mergeWithArrayOverride } from '@yimoka/shared';

import { opStoreAfterAtFetch } from './aop';
import type { IStoreAPI, IStoreHTTPRequest } from './api';
import { BaseStore } from './base';
import { IFieldsConfig } from './field';
import { ListStore } from './list';
import type { ISchema } from './schema';

import type { IStore, IStoreConfig } from '.';

const ENTITY_STORE_MAP = {
  base: BaseStore,
  list: ListStore,
};

/**
 * 获取实体存储实例
 * @param store - 存储实例或配置
 * @param mode - API操作模式
 * @param config - 实体配置
 * @param isOperation - 是否为操作模式
 * @returns 存储实例或配置
 * @remarks 根据不同的操作模式配置存储实例
 */
// eslint-disable-next-line complexity
export function getEntityStore<V extends object = IAnyObject, R extends object = IAnyObject>(store?: IStore<V, R> | IStoreConfig<V, R>, mode?: IAPIKey, config?: IEntityConfig<V>, isOperation?: boolean): IStore<V, R> | IStoreConfig<V, R> {
  if (store instanceof BaseStore) {
    if (!store.apiExecutor && config?.apiExecutor) {
      // eslint-disable-next-line no-param-reassign
      store.apiExecutor = config?.apiExecutor;
    }
    return store;
  }

  const conf: IStoreConfig<IAny, IAny> = {
    dictConfig: config?.dictConfig,
    fieldsConfig: config?.fieldsConfig,
    apiExecutor: config?.apiExecutor,
  };

  const idKey = config?.idKey ?? 'id';
  if (['add', 'edit'].includes(`${mode}`)) {
    conf.afterAtFetch = { ...opStoreAfterAtFetch };
    conf.defaultValues = { ...config?.defaultFormValues };
    // 如果是 edit 添加 idKey
    if (mode === 'edit') {
      if (!(idKey in conf.defaultValues)) {
        conf.defaultValues[idKey] = undefined;
      }
    }
  } else if (['list', 'query'].includes(`${mode}`)) {
    conf.defaultValues = { ...config?.defaultQueryValues };
    conf.type = 'list';
  } else if (['detail'].includes(`${mode}`)) {
    // 默认过滤空值
    if (typeof conf.options === 'undefined') {
      conf.options = {};
    }

    if (typeof conf.options.filterBlankAtRun === 'undefined') {
      conf.options.filterBlankAtRun = true;
    }
    conf.defaultValues = { ...config?.defaultDetailValues };

    if (!(idKey in conf.defaultValues)) {
      conf.defaultValues[idKey] = undefined;
    }
  } else if (isOperation) {
    conf.afterAtFetch = { ...opStoreAfterAtFetch };
  }

  const curStore = store ?? {};

  curStore.dictConfig = curStore.dictConfig ?? conf.dictConfig;
  curStore.fieldsConfig = { ...conf.fieldsConfig, ...curStore.fieldsConfig };
  curStore.defaultValues = { ...conf.defaultValues, ...curStore.defaultValues };
  curStore.afterAtFetch = { ...conf.afterAtFetch, ...curStore.afterAtFetch };
  curStore.options = { ...conf.options, ...curStore.options };
  if  (!curStore.apiExecutor && conf.apiExecutor) {
    curStore.apiExecutor = conf.apiExecutor;
  }

  curStore.type = curStore.type ?? conf.type;

  if (!curStore.api && mode) {
    curStore.api = config?.api?.[mode];
  }

  return curStore;
}

/**
 * 刷新父级存储实例的数据
 * @param parentStore - 父级存储实例，可以是 BaseStore 或 ListStore
 * @param scope - 作用域对象，可能包含 $detailStore 等子存储实例
 * @returns Promise<IStoreResponse | null> 异步操作，返回刷新结果或 null
 * @remarks
 * 当弹窗编辑内容成功后，可刷新父级存储实例的数据。
 * 根据父级存储实例的类型采用不同的刷新策略：
 * - 如果父级是 ListStore，则直接调用 fetch() 方法更新响应数据，返回 fetch 结果
 * - 如果父级是 BaseStore，则优先使用 scope.$detailStore.fetch() 获取数据，
 *   如果没有 $detailStore 则使用 parentStore.fetch()，成功后将数据设置到父级存储实例中并返回结果
 * - 如果没有父级存储实例或操作失败，则返回 null
 * @example
 * ```ts
 * // 刷新列表存储
 * const listStore = new ListStore();
 * const result = await refreshParentStore(listStore);
 * console.log(result); // { code: 0, msg: 'success', data: [...] }
 *
 * // 刷新基础存储，使用详情存储获取数据
 * const baseStore = new BaseStore();
 * const scope = { $detailStore: detailStore };
 * const result = await refreshParentStore(baseStore, scope);
 * console.log(result); // { code: 0, msg: 'success', data: {...} }
 * ```
 * @since 1.0.0
 * @category Entity
 * @group Store
 */
export const refreshParentStore = async (parentStore?: IStore, scope?: IAnyObject) => {
  if (parentStore) {
    // 如果是 ListStore，直接调用 fetch 方法刷新列表数据
    if (parentStore instanceof ListStore) {
      return parentStore.fetch();
    }
    // 如果是 BaseStore，优先使用详情存储获取数据，否则使用父级存储自身
    const res = await (scope?.$detailStore?.fetch() ?? parentStore.fetch());
    if (isSuccess(res)) {
      // 将获取到的数据设置到父级存储实例中
      parentStore.setValues(res.data);
      return res;
    }
  }
  return null;
};

/**
 * 实体配置接口
 * @interface IEntityConfig
 * @remarks 定义实体相关的配置项
 * @template V - 实体数据类型
 */
export interface IEntityConfig<V extends object = IAnyObject> {
  /** 实体名称 */
  name?: string;
  /** 实体的唯一标识字段，默认 id */
  idKey?: string;
  /** 批量操作的唯一标识字段，默认 ids */
  idsKey?: string;
  /** 实体的显示字段，默认 name */
  labelKey?: string;
  /** 基础路径 */
  basePath?: string;
  /** 路径配置，如 add: '/add' edit: '/edit' */
  path?: Record<IAPIKey, string>;
  /** 页面的面包屑配置 */
  breadcrumb?: IBreadcrumbItem[];
  /** 字段配置 */
  fieldsConfig?: IFieldsConfig;
  /** 接口配置，支持请求配置对象或函数式 API */
  api?: Record<IAPIKey, IStoreAPI>;
  /** API 执行器，作为各 Store 的默认执行器 */
  apiExecutor?: IStoreHTTPRequest<IAny, V | IAnyObject>;
  /** 字典 map 映射配置，如 status: { 1: '启用', 0: '禁用' } */
  map?: Record<IObjKey, IAnyObject>;
  /** 字典选项配置，如 status: [{ label: '启用', value: 1 }, { label: '禁用', value: 0 }] */
  options?: Record<IObjKey, IOptions>;
  /** 表单默认值，用于添加/编辑页面，编辑会多一个 idKey */
  defaultFormValues?: Partial<V>;
  /** 查询默认参数的值，用于列表/查询页面 */
  defaultQueryValues?: Partial<V> & IAnyObject;
  /** 详情默认参数的值，用于详情页面 */
  defaultDetailValues?: IAnyObject;

  /** 国际化配置 */
  i18nFieldMap?: Record<IObjKey, IObjKey>;
  i18nInSchema?: ISchema;
  i18nOutSchema?: ISchema;
  /** 多语言下拉选项接口配置，不配置则回退到组件内置语言列表 */
  i18nLangAPI?: IStoreAPI;

  [key: IObjKey]: IAny;
}

/**
 * 面包屑项接口
 * @interface IBreadcrumbItem
 * @remarks 定义面包屑导航项的配置
 */
export interface IBreadcrumbItem {
  /** 显示文本 */
  label?: string;
  /** 图标 */
  icon?: string;
  /** 链接地址 */
  href?: string;
  /** 路径 */
  path?: string;
  [key: IObjKey]: IAny;
}

/**
 * API操作模式类型
 * @type IAPIKey
 * @remarks 定义所有可用的API操作模式
 */
export type IAPIKey =
  | 'add' | 'edit' | 'detail' | 'del' | 'delOne'
  | 'list' | 'queryOne' | 'query' | 'count'
  | 'getCache' | 'setCache' | 'delCache'
  | 'enableOne' | 'disableOne' | 'enable' | 'disable'
  | string;

// ─── Entity 协调器 ──────────────────────────────────────

/** 操作实例的唯一标识，默认 'default' */
export type IStoreKey = string;

/**
 * 获取操作实例的选项
 * 继承 IStoreConfig，支持扁平传入所有 Store 配置（defaultValues、api、options 等）
 */
export interface IGetStoreOptions<V extends object = IAnyObject, R = IAny> extends IStoreConfig<V, R> {
  /** 实例标识，用于多实例并存场景，默认 'default' */
  instanceKey?: IStoreKey;
  /** 是否分页，仅 list 类型有效 */
  isPaginate?: boolean;
  /** 与实体配置深合并，默认 false 不合并，直接用用户配置覆盖 */
  merge?: boolean;
}

/** 获取长尾动作实例的选项 */
export interface IGetOperationOptions<V extends object = IAnyObject, R = IAny> extends IGetStoreOptions<V, R> {
  /** 动作类型，如 delete / freeze / enable / disable */
  opType: string;
}

/**
 * 解析实例标识，空值统一兜底为 'default'
 * @param instanceKey - 实例标识
 * @returns 有效的实例标识
 */
const resolveKey = (instanceKey?: IStoreKey): IStoreKey => instanceKey || 'default';

/**
 * 生成实例池中 operation 类型的键
 * @param opType - 操作类型
 * @returns 实例池键
 */
const getOperationPoolKey = (opType: string) => `operation:${opType}`;

/**
 * 实体协调器
 *
 * 以业务实体为中心，统一管理操作实例（list/add/edit/detail/operation）的创建、复用与销毁。
 * 它是业务概念的代表和协调器，而非状态容器。
 *
 * @template V - 实体数据类型
 *
 * @example
 * ```ts
 * const tenant = new Entity({
 *   name: 'Tenant',
 *   idKey: 'id',
 *   api: {
 *     list: { url: '/api/tenants' },
 *     add: { url: '/api/tenants', method: 'POST' },
 *   },
 *   fieldsConfig: { name: { type: 'string' } },
 *   defaultFormValues: { name: '' },
 *   defaultQueryValues: { page: 1, pageSize: 10 },
 * });
 *
 * const listStore = tenant.getList();
 * const addStore = tenant.getAdd();
 * tenant.disposeList();
 * tenant.disposeAll();
 * ```
 */
export class Entity<V extends object = IAnyObject> {
  /** 实体名称 */
  readonly name: string;

  /** 实体配置 */
  readonly config: IEntityConfig<V>;

  /**
   * 二维实例池
   * 第一维：操作类型（list / add / edit / detail / operation:{opType}）
   * 第二维：实例标识（默认 'default'）
   */
  private pool = new Map<string, Map<IStoreKey, IStore>>();

  constructor(config: IEntityConfig<V>) {
    this.config = config;
    this.name = config.name ?? '';
  }

  // ─── 语义化获取方法 ───────────────────────────────────

  /**
   * 获取列表操作实例
   * @template SV - 自定义值类型，默认使用实体的 V
   * @template SR - 自定义响应类型，默认 IAny
   * @param options - 获取选项，支持扁平传入 Store 配置
   * @returns ListStore 实例
   */
  getList<SV extends object = IAnyObject, SR = IPageData<V>>(options?: IGetStoreOptions<SV, SR>): ListStore<SV, SR> {
    return this.getOrCreate<SV, SR>('list', options) as ListStore<SV & IAnyObject, SR>;
  }

  /**
   * 获取添加操作实例
   * @template SV - 自定义值类型，默认使用实体的 V
   * @template SR - 自定义响应类型，默认 IAny
   * @param options - 获取选项，支持扁平传入 Store 配置
   * @returns BaseStore 实例
   */
  getAdd<SV extends object = V, SR =  V>(options?: IGetStoreOptions<SV, SR>): BaseStore<SV & IAnyObject, SR> {
    return this.getOrCreate<SV, SR>('add', options) as BaseStore<SV & IAnyObject, SR>;
  }

  /**
   * 获取编辑操作实例
   * @template SV - 自定义值类型，默认使用实体的 V
   * @template SR - 自定义响应类型，默认 IAny
   * @param options - 获取选项，支持扁平传入 Store 配置
   * @returns BaseStore 实例
   */
  getEdit<SV extends object = V, SR = V>(options?: IGetStoreOptions<SV, SR>): BaseStore<SV & IAnyObject, SR> {
    return this.getOrCreate<SV, SR>('edit', options) as BaseStore<SV & IAnyObject, SR>;
  }

  /**
   * 获取查询操作实例（与 list 类似，均为 ListStore）
   * @template SV - 自定义值类型，默认使用实体的 V
   * @template SR - 自定义响应类型，默认 IAny
   * @param options - 获取选项，支持扁平传入 Store 配置
   * @returns ListStore 实例
   */
  getQuery<SV extends object = IAnyObject, SR = V[]>(options?: IGetStoreOptions<SV, SR>): BaseStore<SV & IAnyObject, SR> {
    return this.getOrCreate<SV, SR>('query', options) as BaseStore<SV & IAnyObject, SR>;
  }

  /**
   * 获取详情操作实例
   * @template SV - 自定义值类型，默认使用实体的 V
   * @template SR - 自定义响应类型，默认 IAny
   * @param options - 获取选项，支持扁平传入 Store 配置
   * @returns BaseStore 实例
   */
  getDetail<SV extends object ={id?:string}, SR = V>(options?: IGetStoreOptions<SV, SR>): BaseStore<SV & IAnyObject, SR> {
    return this.getOrCreate<SV, SR>('detail', options) as BaseStore<SV & IAnyObject, SR>;
  }

  /**
   * 获取长尾动作操作实例（delete / freeze / enable / disable 等）
   * @template SV - 自定义值类型 自定义操作 必填
   * @template SR - 自定义响应类型 自定义操作 必填
   * @param options - 获取选项，必须包含 opType
   * @returns BaseStore 实例
   */
  getOperation<SV extends object, SR>(options: IGetOperationOptions<SV, SR>): BaseStore<SV & IAnyObject, SR> {
    const poolKey = getOperationPoolKey(options.opType);
    return this.getOrCreate<SV, SR>(poolKey, options, options.opType) as BaseStore<SV & IAnyObject, SR>;
  }

  // ─── 存在性检测 ────────────────────────────────────────

  hasList(instanceKey?: IStoreKey): boolean {
    return this.has('list', instanceKey);
  }

  hasAdd(instanceKey?: IStoreKey): boolean {
    return this.has('add', instanceKey);
  }

  hasEdit(instanceKey?: IStoreKey): boolean {
    return this.has('edit', instanceKey);
  }

  hasQuery(instanceKey?: IStoreKey): boolean {
    return this.has('query', instanceKey);
  }

  hasDetail(instanceKey?: IStoreKey): boolean {
    return this.has('detail', instanceKey);
  }

  hasOperation(opType: string, instanceKey?: IStoreKey): boolean {
    return this.has(getOperationPoolKey(opType), instanceKey);
  }

  // ─── 销毁方法 ──────────────────────────────────────────

  disposeList(instanceKey?: IStoreKey): void {
    this.dispose('list', instanceKey);
  }

  disposeAdd(instanceKey?: IStoreKey): void {
    this.dispose('add', instanceKey);
  }

  disposeEdit(instanceKey?: IStoreKey): void {
    this.dispose('edit', instanceKey);
  }

  disposeQuery(instanceKey?: IStoreKey): void {
    this.dispose('query', instanceKey);
  }

  disposeDetail(instanceKey?: IStoreKey): void {
    this.dispose('detail', instanceKey);
  }

  disposeOperation(opType: string, instanceKey?: IStoreKey): void {
    this.dispose(getOperationPoolKey(opType), instanceKey);
  }

  // ─── 重置方法（dispose + 重新创建）─────────────────────

  resetList<SV extends object = IAnyObject, SR = IPageData<V>>(options?: IGetStoreOptions<SV, SR>): ListStore<SV & IAnyObject, SR> {
    this.disposeList(options?.instanceKey);
    return this.getList<SV, SR>(options);
  }

  resetAdd<SV extends object = V, SR = V>(options?: IGetStoreOptions<SV, SR>): BaseStore<SV & IAnyObject, SR> {
    this.disposeAdd(options?.instanceKey);
    return this.getAdd<SV, SR>(options);
  }

  resetEdit<SV extends object = V, SR = V>(options?: IGetStoreOptions<SV, SR>): BaseStore<SV & IAnyObject, SR> {
    this.disposeEdit(options?.instanceKey);
    return this.getEdit<SV, SR>(options);
  }

  resetQuery<SV extends object = IAnyObject, SR = V[]>(options?: IGetStoreOptions<SV, SR>): BaseStore<SV & IAnyObject, SR> {
    this.disposeQuery(options?.instanceKey);
    return this.getQuery<SV, SR>(options);
  }

  resetDetail<SV extends object ={id?:string}, SR = V>(options?: IGetStoreOptions<SV, SR>): BaseStore<SV & IAnyObject, SR> {
    this.disposeDetail(options?.instanceKey);
    return this.getDetail<SV, SR>(options);
  }

  resetOperation<SV extends object, SR>(options: IGetOperationOptions<SV, SR>): BaseStore<SV & IAnyObject, SR> {
    this.disposeOperation(options.opType, options.instanceKey);
    return this.getOperation<SV, SR>(options);
  }

  // ─── 通用底层 API ─────────────────────────────────────

  /**
   * 底层获取或创建实例
   * @template SV - 自定义值类型，默认使用实体的 V
   * @template SR - 自定义响应类型，默认 IAny
   * @param type - 实例池键（list / add / edit / detail / operation:{opType}）
   * @param options - 获取选项，包含 instanceKey 和自定义 Store 配置
   * @param opType - 仅 operation 类型需要，用于传递给 getEntityStore
   * @returns Store 实例
   */
  getOrCreate<SV extends object = V, SR = IAny>(type: string, options?: IGetStoreOptions<SV, SR>, opType?: string): IStore<SV & IAnyObject, SR> {
    const key = resolveKey(options?.instanceKey);
    const existing = this.get<SV, SR>(type, key);
    if (existing) {
      return existing;
    }
    const store = this.createStore<SV, SR>(type, options, opType);
    if (!this.pool.has(type)) {
      this.pool.set(type, new Map());
    }
    this.pool.get(type)!.set(key, store as IStore);
    return store;
  }

  /**
   * 底层只查方法
   * @template SV - 自定义值类型，默认使用实体的 V
   * @template SR - 自定义响应类型，默认 IAny
   * @param type - 实例池键
   * @param instanceKey - 实例标识
   */
  get<SV extends object = V, SR = IAny>(type: string, instanceKey?: IStoreKey): IStore<SV & IAnyObject, SR> | undefined {
    return this.pool.get(type)?.get(resolveKey(instanceKey)) as IStore<SV & IAnyObject, SR> | undefined;
  }

  /**
   * 底层存在性检测
   * @param type - 实例池键
   * @param instanceKey - 实例标识
   */
  has(type: string, instanceKey?: IStoreKey): boolean {
    return this.pool.get(type)?.has(resolveKey(instanceKey)) ?? false;
  }

  /**
   * 底层销毁指定实例
   * @param type - 实例池键
   * @param instanceKey - 实例标识
   */
  dispose(type: string, instanceKey?: IStoreKey): void {
    const key = resolveKey(instanceKey);
    const typePool = this.pool.get(type);
    if (typePool) {
      typePool.delete(key);
      if (typePool.size === 0) {
        this.pool.delete(type);
      }
    }
  }

  /**
   * 批量销毁实例
   * @param type - 可选，指定类型则只销毁该类型所有实例，不传则销毁全部
   */
  disposeAll(type?: string): void {
    if (type) {
      this.pool.delete(type);
    } else {
      this.pool.clear();
    }
  }

  // ─── 内部实现 ──────────────────────────────────────────

  /**
   * 根据操作类型创建 Store 实例
   * @template SV - 自定义值类型，默认使用实体的 V
   * @template SR - 自定义响应类型，默认 IAny
   * @param poolKey - 实例池键
   * @param options - 用户自定义 Store 配置
   * @param opType - operation 动作类型（仅 operation 类型使用）
   */
  private createStore<SV extends object = V, SR = IAny>(poolKey: string, options?: IGetStoreOptions<SV, SR>, opType?: string): IStore<SV & IAnyObject, SR> {
    const mode = opType ?? poolKey;
    const { instanceKey, isPaginate, merge, ...storeOptions } = options ?? {};
    const entityStoreConfig = getEntityStore({}, mode, this.config, !!opType) as IStoreConfig;
    const storeConfig = merge
      ? mergeWithArrayOverride<IStoreConfig>({}, entityStoreConfig, storeOptions)
      : { ...entityStoreConfig, ...storeOptions };
    const finalConfig: IAny = { ...storeConfig, isPaginate };
    const storeType = (storeConfig.type ?? 'base') as keyof typeof ENTITY_STORE_MAP;
    return new ENTITY_STORE_MAP[storeType](finalConfig) as IStore<SV & IAnyObject, SR>;
  }
}
